import React from 'react';

import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useSetAtom } from 'jotai';

import { ToothIcon } from '../../../../common-components/elements/icon/svgs/ToothIcon';
import {
  blueBlack630,
  greyLight300,
  orange700,
  red400,
  white,
  yellow500,
} from '../../../../theme/videa-style-tokens';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import { selectedAnalysesAtom } from '../../atoms';
import { useIsScheduleViewEnabled } from '../../helpers';
import { VideaAvatar } from '../styles';

import * as Styled from './styles';

interface Props {
  type: 'Perio' | 'Resto';
  label?: string;
  unplannedFindings?: boolean;
  hasUnknown?: boolean;
  toothNumbers?: string[];
  pathologyCount?: number | string;
  quadrantColors?: QuadrantColors | null;
  imagesDate?: string | null;
  patientId?: string;
  analyses?: string[];
  className?: string;
}

type QuadrantColors = (string | null)[];

interface PerioQuadsProps {
  quadrantColors: QuadrantColors;
  isScheduleViewEnabled: boolean;
}

export const QuadrantPerioIndicator: React.FC<PerioQuadsProps> = ({
  quadrantColors,
  isScheduleViewEnabled,
}) => {
  const PerioQuadrants = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1px;
    transform: translateX(0.125em);

    .arch {
      align-items: center;
      display: flex;
      height: 0.5em;
      justify-content: space-between;
      gap: 1px;

      .quadrant {
        background-color: ${white};
        width: 0.5em;
        height: 0.5em;

        &.severe {
          background-color: ${red400};
        }
        &.moderate {
          background-color: ${orange700};
        }
        &.mild {
          background-color: ${yellow500};
        }
        &.normal {
          background-color: ${greyLight300};
        }
      }

      &.top {
        .quadrant-1 {
          border-radius: 0.3125em 0.125em 0 0.125em;
        }
        .quadrant-2 {
          border-radius: 0.125em 0.3125em 0.125em 0;
        }
      }

      &.bottom {
        flex-direction: row-reverse;
        .quadrant-3 {
          border-radius: 0 0.125em 0.3125em 0.125em;
        }
        .quadrant-4 {
          border-radius: 0.125em 0 0.125em 0.3125em;
        }
      }
    }
  `;

  const perioColors = quadrantColors.map((color, i) =>
    !color ? 'quadrant' : `quadrant quadrant-${i + 1} ${color}`,
  );

  return (
    <PerioQuadrants
      className={classNames({ 'schedule-view': isScheduleViewEnabled })}
    >
      <div className="arch top">
        <div key="quad-1" className={perioColors[0]}></div>
        <div key="quad-2" className={perioColors[1]}></div>
      </div>
      <div className="arch bottom">
        <div key="quad-3" className={perioColors[2]}></div>
        <div key="quad-4" className={perioColors[3]}></div>
      </div>
    </PerioQuadrants>
  );
};

export const FindingChip = ({
  type,
  pathologyCount,
  unplannedFindings,
  hasUnknown,
  toothNumbers,
  label,
  quadrantColors,
  imagesDate,
  patientId,
  analyses,
  className: chipClass,
}: Props) => {
  const { displayToothNumber } = useUniversalToFDI();
  const isScheduleViewEnabled = useIsScheduleViewEnabled();
  const setSelectedAnalyses = useSetAtom(selectedAnalysesAtom);
  const quadsWithPathologies = quadrantColors?.filter(
    (color) => color !== 'normal',
  );
  const { t } = useLingui();
  const perioTipSummary =
    quadsWithPathologies?.length && !isScheduleViewEnabled
      ? `${quadsWithPathologies?.length} ${t`Quad(s)`}`
      : null;

  const restoTipSummary = toothNumbers
    ? toothNumbers.map(displayToothNumber).join(' • ')
    : '';

  const generateTip = () => {
    switch (type) {
      case 'Perio':
        return unplannedFindings
          ? `${t`AI perio findings potentially requiring treatment but with none planned/completed`} ${
              imagesDate
                ? `${t`since x-ray date:`} ${imagesDate}`
                : t`since previous images`
            }. ${!!perioTipSummary && ` ${perioTipSummary}.`}`
          : `${t`AI perio findings with planned or completed treatment recorded`} ${
              imagesDate
                ? `${t`since x-ray date:`} ${imagesDate}`
                : t`since previous images`
            }. ${!!perioTipSummary && ` ${perioTipSummary}.`}`;
      case 'Resto':
        if (hasUnknown)
          return t`AI findings for teeth that could not be identified.`;
        return `${
          unplannedFindings
            ? `${t`AI resto findings potentially requiring treatment but with none planned/completed`} ${
                imagesDate
                  ? `${t`since x-ray date:`} ${imagesDate}`
                  : t`since previous images`
              }. ${!!restoTipSummary && ` ${t`Tooth number(s)`} ${restoTipSummary}`}`
            : `${t`AI resto findings with planned or completed treatment recorded`} ${
                imagesDate
                  ? `${t`since x-ray date:`} ${imagesDate}`
                  : t`since previous images`
              }. ${!!restoTipSummary && ` ${t`Tooth number(s)`} ${restoTipSummary}`}`
        }`;
      default:
        return '';
    }
  };

  /**
   * if we have a label, it will be to identify matched and unplanned
   * if we don't get a label, generate list of all findings for perio and resto
   */
  const shouldShowGenericFindings =
    (type === 'Perio' && !label) || (type === 'Resto' && !label);

  /**
   * if needed, generate a generic label
   * depending on the type of finding
   * if there are more than 13 tooth findings, truncate and add ellipsis
   */
  const MAX_TEETH_NUMBER = 13;
  const generateGenericLabel = () => {
    if (type === 'Resto' && (!toothNumbers || !toothNumbers.length))
      return <></>;
    const teethNumbersTruncated =
      type === 'Resto' &&
      !!toothNumbers &&
      toothNumbers.length > MAX_TEETH_NUMBER
        ? toothNumbers.slice(0, MAX_TEETH_NUMBER)
        : toothNumbers;

    const toothElements =
      typeof teethNumbersTruncated === 'object'
        ? teethNumbersTruncated?.map((tooth: string, i: number) => (
            <div className="tooth" key={i}>
              {displayToothNumber(tooth)}
            </div>
          ))
        : null;

    const toothList = (
      <div className="tooth-list">
        {toothElements}
        {!!toothNumbers && toothNumbers.length > MAX_TEETH_NUMBER ? (
          <div className="tooth">...</div>
        ) : null}
      </div>
    );
    switch (type) {
      case 'Perio':
        return <>{perioTipSummary}</>;
      case 'Resto':
        return (
          <div className="tooth-numbers">
            <ToothIcon />
            {isScheduleViewEnabled ? toothNumbers?.length : toothList}
          </div>
        );
      default:
        return <></>;
    }
  };

  const flattenedLabel = shouldShowGenericFindings
    ? generateGenericLabel()
    : label;

  const perioAvatar = (
    <>
      <QuadrantPerioIndicator
        quadrantColors={
          quadrantColors ? quadrantColors : ([] as QuadrantColors)
        }
        isScheduleViewEnabled={isScheduleViewEnabled}
      />
    </>
  );
  const restoAvatar = (
    <VideaAvatar
      background={unplannedFindings ? orange700 : blueBlack630}
      className="small-count"
    >
      {pathologyCount}
    </VideaAvatar>
  );

  /**
   * For generic findings, don't show a tooltip
   */
  const flattenedTip = !shouldShowGenericFindings ? (
    <Tooltip title={generateTip()}>
      <Styled.InfoOutlinedIcon />
    </Tooltip>
  ) : (
    <></>
  );

  const chipClasses = classNames({
    matched: !shouldShowGenericFindings && !unplannedFindings,
    unplanned: !shouldShowGenericFindings && !!unplannedFindings,
    generic: shouldShowGenericFindings,
    'perio-chip': type === 'Perio',
    'resto-chip': type === 'Resto',
    'schedule-view': isScheduleViewEnabled,
    'has-thumbnail': !!analyses?.length && patientId,
    [chipClass || '']: true,
  });

  return (
    <Styled.VideaChipFilled
      label={flattenedLabel}
      data-cy={
        analyses?.length && patientId ? 'unplanned-button' : 'tx-matched'
      }
      data-testid={
        analyses?.length && patientId ? 'unplanned-button' : 'tx-matched'
      }
      className={chipClasses}
      avatar={type === 'Perio' ? perioAvatar : restoAvatar}
      deleteIcon={flattenedTip}
      onDelete={() => null}
      onClick={
        analyses?.length && patientId
          ? (e) => {
              setSelectedAnalyses({
                selectedAnalyses: analyses,
                unplannedQuadrantToothNumbers:
                  type === 'Resto' ? toothNumbers : quadrantColors,
                patientId,
                findingType: type,
                anchorEl: e.currentTarget,
              });
            }
          : undefined
      }
    />
  );
};
